import { FC } from "react";

import styled from "@emotion/styled";
import { cmsImageUrl } from "src/lib/cmsImageUrl";
import translate from "src/lib/translate";
import { IconPlus } from "src/ui/assets/icons/IconPlus";
import Translate from "src/ui/components/Translate/Translate";

import MealAppDownloadBox from "./MealAppDownloadBox";

// Fanned meal photos at the bottom of the card. Decorative only — served through
// the CMS image proxy like the sibling meal cards (MealHeroBackground /
// RxDietCard). All three plates are the same size, so one param set covers them:
// the source assets are 2752x1536 PNGs but we render them as ~184px squares, so
// we let the proxy crop, resize to ~2x the display box, and serve WebP — this
// drops each from multiple MB to tens of KB on slow connections.
const PHOTO_PARAMS = {
  width: 380,
  height: 380,
  fit: "crop",
  crop: "center",
  autoFormat: true,
  quality: 75
} as const;

const mealPhotoLeft = cmsImageUrl(
  "7111a0a90f0018dcb96373ce3843ddb99192d63f-2752x1536.png",
  PHOTO_PARAMS
);
const mealPhotoCenter = cmsImageUrl(
  "e863a1a2cac6e738d2119096919ac3cfc354b7de-2752x1536.png",
  PHOTO_PARAMS
);
const mealPhotoRight = cmsImageUrl(
  "70a602fa4fadaa2011c5723123fe2a46bea12790-2752x1536.png",
  PHOTO_PARAMS
);

// iOS Safari stops clipping to the border-radius wherever a descendant has a
// CSS filter (Photos' drop-shadow) overlapping the edge — the fanned photos
// hang past the bottom via negative offsets and show square corners instead
// of being clipped. The -webkit-mask-image forces the mask-based clip path,
// which fixes it.
const Card = styled.button`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  /* The card owns the screen on both breakpoints: it grows into whatever the
     Meals page has left below the header. The per-variant min-heights are the
     card's own content height, so a short viewport scrolls instead of squashing
     the fan into the headline. */
  flex: 1 0 auto;
  width: 100%;
  margin: 0;
  padding: 1.5rem 1.5rem 0;
  border: none;
  overflow: hidden;
  cursor: default;
  background: var(--color-white, #fff);
  border-radius: 1.5rem;
  -webkit-mask-image: -webkit-radial-gradient(white, black);

  &[data-interactive="true"] {
    cursor: pointer;
  }

  &[data-variant="mobile"] {
    min-height: 23.875rem;
  }

  &[data-variant="desktop"] {
    min-height: 27.375rem;
    padding: 3rem 1.5rem 0;
  }
`;

// The wash is anchored to the top in pixels and resolves to the card's white by
// ~300px, filling the full card height. This keeps the fade-out independent of
// how tall the card is — on desktop the headline wraps to fewer lines, so a
// fixed-height gradient would leave its tail peeking out behind the photos.
const Background = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    #eee6ff 0,
    #dccbff 16px,
    #eee6ff 196px,
    var(--color-white, #fff) 300px
  );
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;

  &[data-variant="desktop"] {
    gap: 1.5rem;
  }
`;

const Pill = styled.span`
  display: flex;
  align-items: center;
  padding: 0.1875rem 0.5rem 0.0625rem;
  color: var(--Accessible-PurpleHaze, #5f1f8f);
  line-height: 0;
`;

const PillLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.33;
  letter-spacing: 0.48px;
  text-transform: uppercase;
`;

const TextGroup = styled.p`
  margin: 0;
  text-align: center;
  text-wrap: balance;
  color: var(--color-charcoal, #212121);
  font-family: "STK Bureau Serif", serif;
  font-weight: 300;

  &[data-variant="desktop"] {
    letter-spacing: -1px;
  }
`;

const Title = styled.span`
  display: block;
  font-size: 1.75rem;
  line-height: 2.125rem;
  letter-spacing: -0.56px;

  [data-variant="desktop"] & {
    font-size: 2.25rem;
    line-height: 2.5rem;
    letter-spacing: -0.54px;
  }
`;

const AddMeal = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  height: 2.75rem;
  padding: 0.375rem 1rem;
  border-radius: 200px;
  background: rgba(33, 33, 33, 0.04);
  color: var(--color-charcoal, #212121);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.33;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

// The fan spans the full card width on mobile — wider than the card's content
// box, so it cancels the 1.5rem side padding and bleeds off both edges (the
// card clips it). Sizes and offsets are percentages of this block, which keeps
// the arrangement proportional as the card widens on larger phones instead of
// leaving the outer photos short of the edges. Desktop keeps the design's fixed
// 358px block inside the wider card, with its own (flatter) proportions.
const Photos = styled.div`
  position: relative;
  width: calc(100% + 3rem);
  aspect-ratio: 327 / 136;
  filter: drop-shadow(-6px 20px 7.5px rgba(90, 68, 0, 0.32))
    drop-shadow(-53px 33px 18.5px rgba(90, 68, 0, 0.16))
    drop-shadow(-94px 59px 22px rgba(90, 68, 0, 0.05));

  &[data-variant="desktop"] {
    width: 358px;
    max-width: 100%;
    aspect-ratio: 358 / 140;
  }
`;

const Photo = styled.img`
  position: absolute;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 1.5rem;
  pointer-events: none;
`;

// All three plates are the same square; only the outer two are tilted. A square
// rotated 10.67deg has a bounding box cos+sin = 1.168x its own size, and the
// design measures that bounding box — so the outer photos' own width is 1/1.168
// of the measured one, and they sit lower, their raised corners bringing them
// level with the centre plate. Sizing them at the measured value instead makes
// them 17% too big and lifts them clear above the middle.
const PhotoLeft = styled(Photo)`
  bottom: -49.28%;
  left: -2.91%;
  width: 56.39%;
  transform: rotate(-10.67deg);

  [data-variant="desktop"] & {
    bottom: -45.7%;
    left: 3.79%;
    width: 44.39%;
  }
`;

const PhotoRight = styled(Photo)`
  bottom: -49.28%;
  right: -2.92%;
  width: 56.39%;
  transform: rotate(10.67deg);

  [data-variant="desktop"] & {
    bottom: -45.7%;
    right: 3.73%;
    width: 44.39%;
  }
`;

const PhotoCenter = styled(Photo)`
  bottom: -35.17%;
  left: 22.02%;
  width: 56.27%;

  [data-variant="desktop"] & {
    bottom: -29.83%;
    left: 27.93%;
    width: 44.13%;
  }
`;

interface MealTodayEmptyCardProps {
  /**
   * Opens the meal photo picker. When omitted (e.g. on desktop, where meal
   * upload isn't offered), the card renders without the "Add a Meal" CTA and is
   * not interactive.
   */
  onAddMeal?: () => void;
  /**
   * Desktop swaps the upload CTA for an app-download box (QR + store links),
   * since meals can only be photographed in the app, and leads with the
   * one-line headline at the wider desktop metrics.
   */
  variant?: "mobile" | "desktop";
}

const MealTodayEmptyCard: FC<MealTodayEmptyCardProps> = ({
  onAddMeal,
  variant = "mobile"
}) => (
  <Card
    as={onAddMeal ? "button" : "div"}
    type={onAddMeal ? "button" : undefined}
    onClick={onAddMeal}
    data-interactive={onAddMeal ? "true" : "false"}
    data-variant={variant}
    aria-label={
      onAddMeal ? translate("mealInsights.today.empty.addMeal") : undefined
    }
  >
    <Background aria-hidden />
    <Content data-variant={variant}>
      <Pill>
        <PillLabel>
          <Translate msg="mealInsights.today.empty.discover" />
        </PillLabel>
      </Pill>
      <TextGroup data-variant={variant}>
        <Title>
          <Translate msg="mealInsights.today.empty.subtitle" />
        </Title>
      </TextGroup>
      {variant === "desktop" && (
        <MealAppDownloadBox msg="mealInsights.today.empty.downloadApp" />
      )}
      {onAddMeal && (
        <AddMeal>
          <IconPlus />
          <Translate msg="mealInsights.today.empty.addMeal" />
        </AddMeal>
      )}
    </Content>
    <Photos aria-hidden data-variant={variant}>
      <PhotoLeft src={mealPhotoLeft} alt="" />
      <PhotoRight src={mealPhotoRight} alt="" />
      <PhotoCenter src={mealPhotoCenter} alt="" />
    </Photos>
  </Card>
);

export default MealTodayEmptyCard;
